import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Heading, Text, SimpleGrid, Stat, StatLabel, StatNumber, Table, Thead,
  Tbody, Tr, Th, Td, TableContainer, Spinner, Alert, AlertIcon, Flex, Badge,
} from '@chakra-ui/react';
import { formatCurrency } from '../utils/currencyUtils';
import dailyReportsService from '../service/DailyReportsService';

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

/**
 * One cash-counter shift's slip: drawer figures + every payment +
 * signature lines. Data is the Payment report scoped to `shiftId`, so
 * the slip and the report can never disagree. Rendered inside the
 * preview modal and on the full-page print route.
 */
const ShiftReportContent = ({ shiftId, onLoaded }) => {
  const { t } = useTranslation('reports');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!shiftId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    dailyReportsService.getPaymentReport(undefined, undefined, 'all', '', { shiftId })
      .then(res => {
        if (cancelled) return;
        const body = res?.data || res;
        setData(body);
        onLoaded?.(body);
      })
      .catch(err => { if (!cancelled) setError(err.message || t('payments.loadFailed')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftId]);

  const shift = data?.shift_filter;
  const summary = data?.summary;
  const cashTotal = (summary?.by_mode || [])
    .filter(m => m.payment_method === 'cash')
    .reduce((n, m) => n + (m.total || 0), 0);
  const expected = shift ? shift.opening_balance + cashTotal : null;

  if (loading) return <Flex justify="center" py={8}><Spinner /></Flex>;
  if (error) return <Alert status="error"><AlertIcon />{error}</Alert>;
  if (!shift) return null;

  return (
    <Box className="shift-print-area">
      <Heading size="md">{t('cashCounter.shiftReport', 'Shift report')}</Heading>
      <Text fontSize="sm" mt={1}>
        {t('cashCounter.cashier', 'Cashier')}: <b>{shift.cashier || '—'}</b>
        {' · '}{t('cashCounter.counter', 'Counter')}: <b>{shift.counter || '—'}</b>
        {shift.shift ? <> · {t('cashCounter.shift', 'Shift')}: <b>{shift.shift}</b></> : null}
        {' · '}<Badge colorScheme={shift.status === 'open' ? 'orange' : 'green'}>{shift.status}</Badge>
      </Text>
      <Text fontSize="sm">
        {fmtTime(shift.opened_at)} – {shift.closed_at ? fmtTime(shift.closed_at) : t('cashCounter.openLabel', 'open')}
      </Text>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} my={4}>
        <Stat><StatLabel fontSize="xs">{t('cashCounter.openingFloat', 'Opening cash float')}</StatLabel>
          <StatNumber fontSize="md">{formatCurrency(shift.opening_balance)}</StatNumber></Stat>
        {(summary?.by_mode || []).map(m => (
          <Stat key={`${m.payment_method}-${m.card_type}`}>
            <StatLabel fontSize="xs">{m.payment_method_display}</StatLabel>
            <StatNumber fontSize="md">{formatCurrency(m.total)}</StatNumber>
            <Text fontSize="xs" color="gray.500">{t('payments.paymentsCount', { count: m.count })}</Text>
          </Stat>
        ))}
        <Stat><StatLabel fontSize="xs">{t('cashCounter.allMethods', 'All methods')}</StatLabel>
          <StatNumber fontSize="md">{formatCurrency(summary?.grand_total)}</StatNumber></Stat>
        <Stat><StatLabel fontSize="xs">{t('cashCounter.expectedCash', 'Expected cash in drawer')}</StatLabel>
          <StatNumber fontSize="md">{formatCurrency(expected)}</StatNumber></Stat>
        {shift.physical_count != null && (
          <>
            <Stat><StatLabel fontSize="xs">{t('cashCounter.countedCash', 'Counted cash')}</StatLabel>
              <StatNumber fontSize="md">{formatCurrency(shift.physical_count)}</StatNumber></Stat>
            <Stat><StatLabel fontSize="xs">{t('cashCounter.variance', 'Variance')}</StatLabel>
              <StatNumber fontSize="md" color={shift.variance < 0 ? 'red.500' : shift.variance > 0 ? 'orange.500' : 'green.500'}>
                {formatCurrency(shift.variance)}
              </StatNumber></Stat>
          </>
        )}
      </SimpleGrid>

      <TableContainer>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>{t('payments.columns.time')}</Th>
              <Th>{t('payments.columns.folioNo')}</Th>
              <Th>{t('payments.columns.guest')}</Th>
              <Th>{t('payments.columns.room')}</Th>
              <Th>{t('payments.columns.paymentMode')}</Th>
              <Th>{t('payments.columns.refNo')}</Th>
              <Th isNumeric>{t('payments.columns.amount')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {(data?.payments || []).map(p => (
              <Tr key={p.id} bg={p.is_refund ? 'red.50' : undefined}>
                <Td fontFamily="mono" fontSize="xs">{p.time}</Td>
                <Td fontFamily="mono" fontSize="xs">{p.folio_no}</Td>
                <Td fontSize="xs">{p.guest_name}</Td>
                <Td fontSize="xs">{p.room_number}</Td>
                <Td fontSize="xs">{p.payment_method_display}{p.card_type ? ` (${p.card_type})` : ''}</Td>
                <Td fontFamily="mono" fontSize="xs">{p.reference || '-'}</Td>
                <Td isNumeric fontSize="xs" color={p.is_refund ? 'red.500' : undefined}>
                  {p.is_refund ? '-' : ''}{formatCurrency(Math.abs(p.amount))}
                </Td>
              </Tr>
            ))}
            {(data?.payments || []).length === 0 && (
              <Tr><Td colSpan={7}><Text fontSize="sm" color="gray.500">{t('cashCounter.noPayments', 'No payments in this shift.')}</Text></Td></Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Text fontSize="xs" color="gray.600" mt={3}>
        {t('payments.printedAt', 'Printed')}: {new Date().toLocaleString()}
      </Text>
      <Flex mt={8} justify="space-between" fontSize="sm">
        <Text>{t('cashCounter.signCashier', 'Cashier signature')}: ______________________</Text>
        <Text>{t('cashCounter.signManager', 'Manager signature')}: ______________________</Text>
      </Flex>
    </Box>
  );
};

export default ShiftReportContent;
